// frontend/src/AdminPaketler.js
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, ListGroup, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import PaketFormu from './PaketFormu';
import api from './api';

const MODUL_ISIMLERI = {
  'ogun_modulu': { name: 'Öğün Modülü', icon: 'restaurant_menu', color: '#10b981' },
  'randevu_modulu': { name: 'Randevu Modülü', icon: 'calendar_today', color: '#60a5fa' },
};

function AdminPaketler() {
  const [paketler, setPaketler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [duzenlenenPaket, setDuzenlenenPaket] = useState(null);

  const paketleriGetir = useCallback(async () => {
    try {
      const response = await api.get('/admin/paketler');
      setPaketler(response.data);
    } catch (err) {
      setError("Paketler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { paketleriGetir(); }, [paketleriGetir]);

  const handleFormSubmit = async (formData) => {
    const isUpdating = !!duzenlenenPaket?.id;
    const url = isUpdating ? `/admin/paketler/${duzenlenenPaket.id}` : '/admin/paketler';
    const method = isUpdating ? 'put' : 'post';
    try {
      await api[method](url, formData);
      setShowModal(false);
      paketleriGetir();
    } catch (error) {
      alert(`Hata: ${error.response?.data?.mesaj || 'İşlem başarısız'}`);
    }
  };
  
  const handleSil = async (paketId) => {
    if (!window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/admin/paketler/${paketId}`);
      paketleriGetir();
    } catch (error) {
      alert(`Hata: ${error.response?.data?.mesaj || 'Silme işlemi başarısız'}`);
    }
  };

  const handleModalOpen = (paket = null) => {
    setDuzenlenenPaket(paket);
    setShowModal(true);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Paket Yönetimi</h2>
          <p className="m-0" style={{ color: 'var(--text-muted)' }}>Paket oluşturun ve modülleri atayarak erişim kontrolü sağlayın.</p>
        </div>
        <Button onClick={() => handleModalOpen()} style={{ backgroundColor: 'var(--accent-primary)', border: 'none', color: '#fff' }} className="fw-bold d-flex align-items-center gap-2">
          <span className="material-symbols-outlined">add</span> Yeni Paket
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <Spinner animation="border" style={{ color: 'var(--accent-primary)' }} />}

      <Row className="g-3">
        {paketler.length > 0 ? paketler.map(paket => (
          <Col md={6} key={paket.id}>
            <Card className="glass-card h-100" style={{ border: 'none' }}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>{paket.ad}</h5>
                    <div className="mt-1" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent-primary)' }}>
                      {paket.fiyat} <span style={{ fontSize: '0.9rem', fontWeight: '500', color: 'var(--text-muted)' }}>TL</span>
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    <Button variant="link" className="p-1" onClick={() => handleModalOpen(paket)} style={{ color: 'var(--accent-primary)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>edit</span>
                    </Button>
                    <Button variant="link" className="p-1" onClick={() => handleSil(paket.id)} style={{ color: '#ef4444' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>delete</span>
                    </Button>
                  </div>
                </div>

                {/* Modüller */}
                <div className="mb-2" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Dahil Modüller</div>
                {(paket.moduller && paket.moduller.length > 0) ? (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {paket.moduller.map(m => {
                      const info = MODUL_ISIMLERI[m] || { name: m, icon: 'extension', color: '#888' };
                      return (
                        <Badge key={m} className="d-flex align-items-center gap-1 px-3 py-2 rounded-pill fw-normal" 
                          style={{ backgroundColor: `${info.color}20`, color: info.color, border: `1px solid ${info.color}40`, fontSize: '0.8rem' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: '0.9rem' }}>{info.icon}</span>
                          {info.name}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mb-3" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>Modül atanmamış</p>
                )}

                {/* Ek Özellikler */}
                {paket.ozellikler && paket.ozellikler.length > 0 && (
                  <>
                    <div className="mb-2" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Ek Özellikler</div>
                    <div className="d-flex flex-wrap gap-1">
                      {paket.ozellikler.map(o => <Badge key={o.id} bg="secondary" className="fw-normal">{o.ad}</Badge>)}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        )) : !loading && (
          <Col>
            <div className="text-center py-5" style={{ color: 'var(--text-muted)' }}>
              <span className="material-symbols-outlined d-block mb-2" style={{ fontSize: '3rem' }}>inventory_2</span>
              Sistemde kayıtlı paket bulunmuyor.
            </div>
          </Col>
        )}
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{duzenlenenPaket?.id ? 'Paket Düzenle' : 'Yeni Paket Oluştur'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PaketFormu 
            mevcutPaket={duzenlenenPaket}
            onFormSubmit={handleFormSubmit}
            onIptal={() => setShowModal(false)}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
export default AdminPaketler;